import { cn } from '@/lib/utils'

/**
 * The five GPU characters of the character-select screen. Each one has its
 * own silhouette, hue and class, drawn as extruded glossy voxels: every pixel
 * is a lit bead with a specular glint, sitting on a dark extrusion so the
 * figure reads as a small 3D toy rather than a flat sprite.
 *
 * Map keys: b = body (character hue), l = highlight, s = shade, p = screen,
 * e = eye, f = face light (glow hue), g = glow accessory, m = metal.
 */

export type HueKey = 'mint' | 'lime' | 'lavender' | 'ember' | 'magenta'

/** Literal class sets per hue, so Tailwind sees every class it needs. */
export const HUES: Record<
  HueKey,
  { cssVar: string; stop: string; text: string; bg: string; soft: string; ring: string; shadow: string; from: string }
> = {
  mint: {
    cssVar: 'var(--chart-3)',
    stop: 'stop-chart-3',
    text: 'text-chart-3',
    bg: 'bg-chart-3',
    soft: 'bg-chart-3/15',
    ring: 'ring-chart-3/60',
    shadow: 'shadow-chart-3/50',
    from: 'from-chart-3',
  },
  lime: {
    cssVar: 'var(--primary)',
    stop: 'stop-primary',
    text: 'text-primary',
    bg: 'bg-primary',
    soft: 'bg-primary/15',
    ring: 'ring-primary/60',
    shadow: 'shadow-primary/50',
    from: 'from-primary',
  },
  lavender: {
    cssVar: 'var(--accent)',
    stop: 'stop-accent',
    text: 'text-accent',
    bg: 'bg-accent',
    soft: 'bg-accent/15',
    ring: 'ring-accent/60',
    shadow: 'shadow-accent/50',
    from: 'from-accent',
  },
  ember: {
    cssVar: 'var(--chart-5)',
    stop: 'stop-chart-5',
    text: 'text-chart-5',
    bg: 'bg-chart-5',
    soft: 'bg-chart-5/15',
    ring: 'ring-chart-5/60',
    shadow: 'shadow-chart-5/50',
    from: 'from-chart-5',
  },
  magenta: {
    cssVar: 'var(--chart-4)',
    stop: 'stop-chart-4',
    text: 'text-chart-4',
    bg: 'bg-chart-4',
    soft: 'bg-chart-4/15',
    ring: 'ring-chart-4/60',
    shadow: 'shadow-chart-4/50',
    from: 'from-chart-4',
  },
}

export interface Persona {
  codename: string
  /** The character's power class. */
  role: string
  tagline: string
  hue: HueKey
  /** Hue of eyes, face lights and accessories. */
  glow: HueKey
  map: readonly string[]
}

// A round chip critter with one big eye, a sprout antenna and pin legs.
const SPARK = [
  '......gg......',
  '......ss......',
  '...bbbbbbbb...',
  '..blllbbbbbb..',
  '..blbbbbbbbb..',
  'm.bbppppppbb.m',
  'mmbbpeepppbbmm',
  'm.bbpeeffpbb.m',
  'mmbbppffppbbmm',
  'm.bbppppppbb.m',
  '..bbbsggsbbb..',
  '...bbbbbbbb...',
  '...ss....ss...',
  '..sss....sss..',
] as const

// A handheld-console striker: screen face, d-pad, buttons and lightning antennae.
const VOLT = [
  '.g............g.',
  '..g..........g..',
  '..bbbbbbbbbbbb..',
  '.bllbbbbbbbbbbb.',
  '.bppppppppppppb.',
  '.bpeeppppppeepb.',
  '.bpeeppppppeepb.',
  '.bppfppppppfppb.',
  '.bpppffffffpppb.',
  'mbbbbbbbbbbbbbbm',
  'mbbsbbbbbbbbbgbm',
  '.bsssbbbbbbgbgb.',
  '.bbsbbbbbbbbbgb.',
  '.bbbbbbbbbbbbbb.',
  '..ss........ss..',
  '.sss........sss.',
] as const

// A hovering capsule mystic with a halo, a visor and a thruster flame.
const NOVA = [
  '......gggg......',
  '.....g....g.....',
  '......gggg......',
  '.....bbbbbb.....',
  '....bllbbbbb....',
  '...bllbbbbbbb...',
  '..bbbbbbbbbbbb..',
  '..bppppppppppb..',
  '..bpeeeppeeepb..',
  '..bppppffppppb..',
  '..bbbbbbbbbbbb..',
  'm..bbbbbbbbbb..m',
  'mm..bbbbbbbb..mm',
  '.....bbbbbb.....',
  '......bggb......',
  '.......gg.......',
  '......g..g......',
] as const

// A wide heavy brawler: small head, shoulder fans, a glowing core and big fists.
const TITAN = [
  '......bbbbbb......',
  '.....bllbbbbb.....',
  '.....bpeppepb.....',
  '.....bpfffffb.....',
  '..sssbbbbbbbbsss..',
  '.sbbbsbbbbbbsbbbs.',
  'sbpppbbllbbbbpppbs',
  'sbpgpbbbbbbbbpgpbs',
  'sbpppbbbggbbbpppbs',
  '.sbbbbbbggbbbbbbs.',
  '.mm.bbbbbbbbbb.mm.',
  'mmmm.bbbbbbbb.mmmm',
  'mmmm.bbsbbsbb.mmmm',
  '.....bbb..bbb.....',
  '....ssss..ssss....',
] as const

// A tall crowned commander with a chest gem and a cape.
const ORACLE = [
  '..g....gg....g..',
  '..gg..gggg..gg..',
  '..gggggggggggg..',
  '..bbbbbbbbbbbb..',
  '..bllbbbbbbbbb..',
  '..bppppppppppb..',
  '..bpeeppppeepb..',
  '..bpppffffpppb..',
  '..bbbbbbbbbbbb..',
  '.ssbbbbggbbbbss.',
  'ssbbbbgffgbbbbss',
  'ssbbbbbggbbbbbss',
  'ssbbbbbbbbbbbbss',
  's.sbbbbbbbbbbs.s',
  's.sbbbbbbbbbbs.s',
  '..sbbbbbbbbbbs..',
  '..ssbbs..sbbss..',
  '..sss......sss..',
] as const

const PERSONAS: Record<string, Persona> = {
  'tesla-t4-lite': {
    codename: 'Spark',
    role: 'Scout',
    tagline: 'Light, nimble and the cheapest way onto the grid.',
    hue: 'mint',
    glow: 'lime',
    map: SPARK,
  },
  'tesla-t4': {
    codename: 'Volt',
    role: 'Striker',
    tagline: 'The all-rounder for long gaming and streaming sessions.',
    hue: 'lime',
    glow: 'lavender',
    map: VOLT,
  },
  'nvidia-l4': {
    codename: 'Nova',
    role: 'Mystic',
    tagline: 'Hovering efficiency for creators and AI inference.',
    hue: 'lavender',
    glow: 'lime',
    map: NOVA,
  },
  'nvidia-a10g': {
    codename: 'Titan',
    role: 'Heavy',
    tagline: 'Raw muscle for AAA gaming, rendering and training runs.',
    hue: 'ember',
    glow: 'lime',
    map: TITAN,
  },
  'nvidia-l4-pro': {
    codename: 'Oracle',
    role: 'Commander',
    tagline: 'The L4 with extra cores and memory for heavy multitasking.',
    hue: 'magenta',
    glow: 'lime',
    map: ORACLE,
  },
}

const FALLBACKS = Object.values(PERSONAS)

/** The persona for a GPU; a GPU added to the catalogue later borrows one by position. */
export function personaFor(gpuId: string, index: number): Persona {
  return PERSONAS[gpuId] ?? FALLBACKS[index % FALLBACKS.length]
}

type Stop = [className: string, opacity: number]

function gradientFor(key: string, persona: Persona): [Stop, Stop, Stop] {
  const hue = HUES[persona.hue].stop
  const glow = HUES[persona.glow].stop
  switch (key) {
    case 'b':
      return [['stop-foreground', 0.7], [hue, 1], ['stop-background', 0.9]]
    case 'l':
      return [['stop-foreground', 1], ['stop-foreground', 0.95], [hue, 1]]
    case 's':
      return [[hue, 0.75], ['stop-secondary', 1], ['stop-background', 1]]
    case 'p':
      return [['stop-accent', 0.3], ['stop-background', 1], ['stop-background', 1]]
    case 'e':
      return [['stop-foreground', 1], ['stop-foreground', 1], [glow, 1]]
    case 'f':
      return [['stop-foreground', 0.8], [glow, 1], [glow, 0.6]]
    case 'g':
      return [['stop-foreground', 0.8], [glow, 1], ['stop-background', 0.8]]
    default:
      return [['stop-foreground', 0.95], ['stop-secondary', 1], ['stop-background', 1]]
  }
}

export function GpuCharacter({
  persona,
  uid,
  title,
  className,
}: {
  persona: Persona
  /** Unique per instance on the page, so gradient ids never collide. */
  uid: string
  title?: string
  className?: string
}) {
  const map = persona.map
  const rows = map.length
  const cols = Math.max(...map.map((row) => row.length))
  const cells = map.flatMap((row, y) =>
    row.split('').flatMap((key, x) => (key === '.' ? [] : [{ key, x, y }])),
  )
  const keys = [...new Set(cells.map((cell) => cell.key))]

  return (
    <svg
      viewBox={`-0.2 -0.2 ${cols + 0.7} ${rows + 0.7}`}
      className={cn('block h-auto w-full overflow-visible', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <defs>
        {keys.map((key) => {
          const stops = gradientFor(key, persona)
          return (
            <linearGradient key={key} id={`${uid}-${key}`} x1="0" y1="0" x2="1" y2="1">
              {stops.map(([className, opacity], index) => (
                <stop key={index} offset={[0, 0.45, 1][index]} className={className} stopOpacity={opacity} />
              ))}
            </linearGradient>
          )
        })}
      </defs>

      {/* extrusion: a hue rim and a dark body behind every voxel */}
      <g className={HUES[persona.hue].text} opacity={0.35}>
        {cells.map(({ x, y }) => (
          <rect key={`r-${x}-${y}`} x={x + 0.3} y={y + 0.32} width={0.92} height={0.92} rx={0.2} fill="currentColor" />
        ))}
      </g>
      <g className="fill-background" opacity={0.9}>
        {cells.map(({ x, y }) => (
          <rect key={`d-${x}-${y}`} x={x + 0.2} y={y + 0.22} width={0.92} height={0.92} rx={0.2} />
        ))}
      </g>

      {keys.map((key) => (
        <g key={key} className={cn(key === 'e' && 'voxel-blink', key === 'g' && 'power-pulse')}>
          {cells
            .filter((cell) => cell.key === key)
            .map(({ x, y }) => (
              <g key={`${x}-${y}`}>
                <rect x={x + 0.04} y={y + 0.04} width={0.92} height={0.92} rx={0.2} fill={`url(#${uid}-${key})`} />
                <rect
                  x={x + 0.16}
                  y={y + 0.14}
                  width={0.36}
                  height={0.16}
                  rx={0.08}
                  className="fill-foreground"
                  opacity={key === 'p' ? 0.12 : 0.55}
                />
              </g>
            ))}
        </g>
      ))}
    </svg>
  )
}
